import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  CampaignDetailSchema,
  CampaignListResponseSchema,
  CampaignPreviewResultSchema,
  CampaignSendResultSchema,
  CampaignStatsSchema,
  SegmentPreviewSchema,
  type Campaign,
  type CampaignDetail,
  type CampaignPreviewResult,
  type CampaignSendResult,
  type CampaignStats,
  type CreateCampaignInput,
  type PatchCampaignInput,
  type Segment,
  type SegmentPreview,
} from "@/api/types/campaign";

/** Query-key factory — mirrors the quotes/contacts hooks' convention. */
export const campaignKeys = {
  all: ["campaigns"] as const,
  lists: () => [...campaignKeys.all, "list"] as const,
  list: (limit: number) => [...campaignKeys.lists(), { limit }] as const,
  details: () => [...campaignKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...campaignKeys.details(), id] as const,
  stats: (id: string | undefined) => [...campaignKeys.all, "stats", id] as const,
  segmentPreview: (segment: Segment) =>
    [...campaignKeys.all, "segment-preview", segment] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: campaignKeys.all });
}

/** Cursor-paginated list, same "load more" convention as useContacts. */
export function useCampaigns(limit = 25) {
  return useInfiniteQuery({
    queryKey: campaignKeys.list(limit),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/campaigns?${searchParams.toString()}`);
      return validateResponse(CampaignListResponseSchema, data, "GET /v2/campaigns");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 15 * 1000,
  });
}

export function useCampaign(campaignId: string | undefined) {
  return useQuery<CampaignDetail>({
    queryKey: campaignKeys.detail(campaignId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/campaigns/${campaignId}`);
      return validateResponse(CampaignDetailSchema, data, "GET /v2/campaigns/:id");
    },
    enabled: !!campaignId,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation<Campaign, unknown, CreateCampaignInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/campaigns", input);
      return validateResponse(CampaignDetailSchema, data, "POST /v2/campaigns");
    },
    onSuccess: (campaign) => {
      queryClient.setQueryData(campaignKeys.detail(campaign.id), campaign);
      invalidateAll(queryClient);
    },
  });
}

export function usePatchCampaign() {
  const queryClient = useQueryClient();
  return useMutation<Campaign, unknown, PatchCampaignInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/campaigns/${id}`, patch);
      return validateResponse(CampaignDetailSchema, data, "PATCH /v2/campaigns/:id");
    },
    onSuccess: (campaign) => {
      queryClient.setQueryData(campaignKeys.detail(campaign.id), campaign);
      invalidateAll(queryClient);
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/v2/campaigns/${id}`);
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
}

/**
 * Live segment preview. Params mirror the documented query string exactly
 * (`kinds=&warm=&tags=`, comma-joined) — there is no consent query param;
 * the consent gate is always applied server-side (see the contract note in
 * api/types/campaign.ts).
 */
export function useSegmentPreview(segment: Segment) {
  const kinds = segment.kinds ?? [];
  const tags = segment.tags ?? [];
  const warm = segment.warm ?? undefined;

  return useQuery<SegmentPreview>({
    queryKey: campaignKeys.segmentPreview({ kinds, tags, warm }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (kinds.length) searchParams.set("kinds", kinds.join(","));
      if (warm) searchParams.set("warm", warm);
      if (tags.length) searchParams.set("tags", tags.join(","));
      const { data } = await apiClient.get(
        `/v2/campaigns/segment-preview?${searchParams.toString()}`,
      );
      return validateResponse(
        SegmentPreviewSchema,
        data,
        "GET /v2/campaigns/segment-preview",
      );
    },
    staleTime: 10 * 1000,
  });
}

/** Renders one recipient's personalized email. Does NOT send. */
export function usePreviewCampaign() {
  return useMutation<
    CampaignPreviewResult,
    unknown,
    { id: string; contact_id?: string }
  >({
    mutationFn: async ({ id, contact_id }) => {
      const { data } = await apiClient.post(`/v2/campaigns/${id}/preview`, {
        ...(contact_id ? { contact_id } : {}),
      });
      return validateResponse(
        CampaignPreviewResultSchema,
        data,
        "POST /v2/campaigns/:id/preview",
      );
    },
  });
}

export function useSendCampaign() {
  const queryClient = useQueryClient();
  return useMutation<CampaignSendResult, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post(`/v2/campaigns/${id}/send`, {});
      return validateResponse(
        CampaignSendResultSchema,
        data,
        "POST /v2/campaigns/:id/send",
      );
    },
    onSuccess: (_result, id) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.stats(id) });
      invalidateAll(queryClient);
    },
  });
}

export function useCampaignStats(campaignId: string | undefined) {
  return useQuery<CampaignStats>({
    queryKey: campaignKeys.stats(campaignId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/campaigns/${campaignId}/stats`);
      return validateResponse(CampaignStatsSchema, data, "GET /v2/campaigns/:id/stats");
    },
    enabled: !!campaignId,
    staleTime: 10 * 1000,
  });
}
